import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Avatar from "@mui/material/Avatar";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Typography from "@mui/material/Typography";
import ContentCopyRoundedIcon from "@mui/icons-material/ContentCopyRounded";
import KeyboardArrowDownRoundedIcon from "@mui/icons-material/KeyboardArrowDownRounded";
import { toast } from "react-toastify";
import { colorCodesForAvatar } from "../../constants/index";
import { chracterPosition } from "../../utils/index";
import PlatformStatusChip from "../../components/platformAdmin/PlatformStatusChip";
import { PlatformBusinessDetailSkeleton } from "../../components/platformAdmin/PlatformLoadingSkeletons";
import { getAvailableActions, usePlatformAdmin } from "../../components/platformAdmin/PlatformAdminContext";
import {
  fetchPlatformBusinessById,
  formatPlatformDate,
  mapBusinessDetail,
} from "../../components/platformAdmin/platformAdminApi";
import {
  cardSx,
  formatNumber,
  InfoField,
  inter,
  menuPaperSx,
  primaryButtonSx,
  SectionCard,
  secondaryButtonSx,
} from "../../components/platformAdmin/platformUi";

function StatTile({ label, value, sub }) {
  return (
    <Box sx={{ ...cardSx, p: 2, minWidth: 0 }}>
      <Typography sx={{ color: "#405261", fontFamily: inter, fontSize: 12 }}>{label}</Typography>
      <Typography sx={{ color: "#061522", fontFamily: inter, fontSize: 20, fontWeight: 500, lineHeight: "26px", mt: 0.5 }}>
        {value}
      </Typography>
      {sub ? <Typography sx={{ color: "#93A1B0", fontFamily: inter, fontSize: 12 }}>{sub}</Typography> : null}
    </Box>
  );
}

export default function PlatformBusinessDetail() {
  const { businessId } = useParams();
  const navigate = useNavigate();
  const { requestAction } = usePlatformAdmin();
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState(null);
  const [menuAnchor, setMenuAnchor] = useState(null);

  const loadDetail = async () => {
    setLoading(true);
    const result = await fetchPlatformBusinessById(businessId);
    if (!result?.success) {
      toast.error(result?.message || "Failed to load business");
      setDetail(null);
    } else {
      setDetail(mapBusinessDetail(result));
    }
    setLoading(false);
  };

  useEffect(() => {
    loadDetail();
  }, [businessId]);

  if (loading) {
    return <PlatformBusinessDetailSkeleton />;
  }

  if (!detail) {
    return (
      <Box sx={{ p: 6, textAlign: "center" }}>
        <Typography sx={{ color: "#061522", fontFamily: inter, fontSize: 18, fontWeight: 500 }}>Business not found</Typography>
        <Button onClick={() => navigate("/platform-admin/businesses")} sx={{ ...secondaryButtonSx, mt: 2 }}>
          Back to Businesses
        </Button>
      </Box>
    );
  }

  const letter = (detail.name || "?")[0].toUpperCase();
  const actions = getAvailableActions(detail);
  const copyId = () => {
    navigator.clipboard?.writeText(detail.id);
    toast.success("Business ID copied");
  };

  const runAction = (key) => {
    setMenuAnchor(null);
    requestAction(key, detail, { onDone: () => loadDetail() });
  };

  return (
    <Box sx={{ p: "24px", display: "flex", flexDirection: "column", gap: 3 }}>
      <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2, flexWrap: "wrap" }}>
        <Avatar
          variant="rounded"
          src={detail.logo || undefined}
          sx={{
            width: 56,
            height: 56,
            fontSize: 22,
            borderRadius: "12px",
            bgcolor: colorCodesForAvatar[chracterPosition(letter)] || "#405261",
          }}
        >
          {letter}
        </Avatar>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
            <Typography sx={{ color: "#061522", fontFamily: inter, fontSize: 22, fontWeight: 500, lineHeight: "28px" }}>
              {detail.name}
            </Typography>
            <PlatformStatusChip status={detail.status} />
          </Box>
          <Typography sx={{ color: "#405261", fontFamily: inter, fontSize: 14, mt: 0.5 }}>
            {detail.slug || "—"} · Created {formatPlatformDate(detail.createdAt)}
          </Typography>
        </Box>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button
            onClick={(e) => setMenuAnchor(e.currentTarget)}
            endIcon={<KeyboardArrowDownRoundedIcon />}
            sx={secondaryButtonSx}
          >
            Actions
          </Button>
          <Menu
            anchorEl={menuAnchor}
            open={Boolean(menuAnchor)}
            onClose={() => setMenuAnchor(null)}
            elevation={0}
            transformOrigin={{ horizontal: "right", vertical: "top" }}
            anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
            MenuListProps={{ disablePadding: true }}
            PaperProps={{ sx: menuPaperSx }}
          >
            {actions.map((a) => (
              <MenuItem
                key={a.key}
                onClick={() => runAction(a.key)}
                sx={a.danger ? { color: "#FF4D4F !important" } : undefined}
              >
                {a.label}
              </MenuItem>
            ))}
          </Menu>
        </Box>
      </Box>

      {(detail.status === "suspended" || detail.status === "banned") && (
        <Box
          sx={{
            p: 2,
            borderRadius: "8px",
            border: "1px solid #FFA39E",
            background: "#FFF1F0",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 2,
          }}
        >
          <Typography sx={{ color: "#405261", fontFamily: inter, fontSize: 14 }}>
            This business is {detail.status}. Agents cannot sign in until it is activated again.
          </Typography>
          <Button onClick={() => runAction("activate")} sx={{ ...primaryButtonSx, flexShrink: 0 }}>
            Activate
          </Button>
        </Box>
      )}

      <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: 2 }}>
        <StatTile
          label="Agents"
          value={`${formatNumber(detail.metrics.agentsActive)} / ${formatNumber(detail.metrics.agentsTotal)}`}
          sub="Active / total"
        />
        <StatTile label="Customers" value={formatNumber(detail.metrics.customersTotal)} />
        <StatTile
          label="Chats"
          value={formatNumber(detail.metrics.chatsOpen)}
          sub={`${formatNumber(detail.metrics.chatsTotal)} total`}
        />
        <StatTile
          label="Tickets"
          value={formatNumber(detail.metrics.ticketsOpen)}
          sub={`${formatNumber(detail.metrics.ticketsTotal)} total`}
        />
      </Box>

      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", lg: "1fr 1fr" }, gap: 2 }}>
        <SectionCard title="Business information">
          <Box sx={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: 2.5 }}>
            <InfoField label="Phone" value={detail.phone} />
            <InfoField label="Email" value={detail.email} />
            <InfoField label="Address" value={detail.address} />
            <InfoField label="Website" value={detail.website} />
            <InfoField label="Staff size" value={detail.staffCount} />
            <InfoField label="Mail domain verified" value={detail.mailDomainVerified ? "Yes" : "No"} />
            <InfoField label="Ticket from email" value={detail.ticketFromEmail} />
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{ color: "#405261", fontFamily: inter, fontSize: 12, mb: 0.75 }}>Business ID</Typography>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <Typography sx={{ color: "#061522", fontFamily: inter, fontSize: 14 }} noWrap>
                  {detail.id}
                </Typography>
                <IconButton size="small" onClick={copyId} sx={{ p: 0.25 }}>
                  <ContentCopyRoundedIcon sx={{ fontSize: 14, color: "#93A1B0" }} />
                </IconButton>
              </Box>
            </Box>
          </Box>
        </SectionCard>

        <SectionCard title="Owner">
          <Box sx={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: 2.5 }}>
            <InfoField label="Name" value={detail.owner.name} />
            <InfoField label="Email" value={detail.owner.email} />
            <InfoField label="Email verified" value={detail.owner.emailVerified ? "Yes" : "No"} />
            <InfoField label="Account active" value={detail.owner.isActive ? "Yes" : "No"} />
            <InfoField label="Joined" value={formatPlatformDate(detail.owner.joiningDate)} />
          </Box>
        </SectionCard>
      </Box>
    </Box>
  );
}
